import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PencilSimple, Trash, Plus, EyeSlash, Eye, Article, Sparkle, ArrowSquareOut } from "@phosphor-icons/react";

function GenerateForm({ templates, onCreated }) {
  const [trade, setTrade] = useState("Plumber");
  const [city, setCity] = useState("Manchester");
  const [tid, setTid] = useState(templates[0]?.id || "signs_you_need");
  const [publish, setPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!trade.trim() || !city.trim()) return toast.error("Trade and city required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {
        trade: trade.trim(), city: city.trim(), template_id: tid, publish,
      });
      toast.success(`Generated: ${data.title.slice(0, 50)}${data.title.length > 50 ? "…" : ""}`);
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Generation failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="brand-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle weight="fill" className="text-[var(--brand-orange)]" size={18} />
        <h3 className="font-display font-bold text-lg text-[var(--brand-green)]">Generate a new article</h3>
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">Powered by Claude Sonnet 4.5 via your Emergent Universal Key. Cost ~£0.05 per article.</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Trade (e.g. Plumber)"
          className="px-3 py-2 rounded border border-[var(--border)] text-sm" data-testid="blog-gen-trade" />
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (e.g. Leeds)"
          className="px-3 py-2 rounded border border-[var(--border)] text-sm" data-testid="blog-gen-city" />
        <select value={tid} onChange={(e) => setTid(e.target.value)}
          className="px-3 py-2 rounded border border-[var(--border)] text-sm" data-testid="blog-gen-template">
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.title_pattern}</option>
          ))}
        </select>
        <button onClick={submit} disabled={busy} className="btn-accent" data-testid="blog-gen-submit">
          <Plus size={14} weight="bold" /> {busy ? "Generating…" : "Generate"}
        </button>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-[var(--text-muted)]">
        <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} data-testid="blog-gen-publish" />
        Publish immediately (skip draft)
      </label>
    </div>
  );
}

function EditModal({ post, onClose, onSaved }) {
  const [f, setF] = useState({
    title: post.title,
    meta_description: post.meta_description,
    excerpt: post.excerpt,
    keywords: (post.keywords || []).join(", "),
    read_minutes: post.read_minutes,
    trade: post.trade,
    city: post.city,
    body_markdown: post.body_markdown || "",
    status: post.status,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/blog/${post.post_id}`, {
        ...f,
        keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        read_minutes: Number(f.read_minutes) || 5,
      });
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--sand)] max-w-3xl w-full max-h-[92vh] overflow-y-auto rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Edit article</h2>
          <button onClick={onClose} className="text-[var(--text-muted)]">✕</button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Title</span>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Meta description (140-160 chars ideally)</span>
            <textarea value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" rows={2} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Excerpt</span>
            <textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" rows={2} />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Trade</span>
              <input value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">City</span>
              <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Read minutes</span>
              <input type="number" value={f.read_minutes} onChange={(e) => setF({ ...f, read_minutes: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Keywords (comma separated)</span>
            <input value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Body (Markdown)</span>
            <textarea value={f.body_markdown} onChange={(e) => setF({ ...f, body_markdown: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1 font-mono text-sm" rows={16} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Status</span>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full px-3 py-2 rounded border border-[var(--border)] mt-1">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy} className="btn-accent flex-1">{busy ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlogTab() {
  const [posts, setPosts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pl, tl] = await Promise.all([
        api.get("/admin/blog"),
        api.get("/admin/blog/templates"),
      ]);
      setPosts(pl.data.items || []);
      setTemplates(tl.data.templates || []);
    } catch (e) {
      // 401 handled globally
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (p) => {
    try {
      await api.patch(`/admin/blog/${p.post_id}`, {
        status: p.status === "published" ? "draft" : "published",
      });
      toast.success(p.status === "published" ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${p.post_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const openEdit = async (p) => {
    try {
      const { data } = await api.get(`/admin/blog/${p.post_id}`);
      setEditing(data);
    } catch { toast.error("Failed to load"); }
  };

  if (loading) return <p className="text-[var(--text-muted)] mt-6">Loading blog…</p>;

  return (
    <div className="mt-6 space-y-5" data-testid="admin-blog-tab">
      <div className="brand-card p-5 flex items-center gap-3">
        <Article size={24} weight="duotone" className="text-[var(--brand-orange)]" />
        <div className="flex-1">
          <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Blog manager</h2>
          <p className="text-sm text-[var(--text-muted)]">Manage articles at <a href="/blog" target="_blank" rel="noreferrer" className="text-[var(--brand-orange)] underline">/blog</a>. Sitemap auto-updates for Google Search Console.</p>
        </div>
      </div>

      <GenerateForm templates={templates} onCreated={load} />

      <div className="brand-card overflow-hidden">
        <div className="p-4 border-b border-[var(--border-stone)]">
          <h3 className="font-display font-bold text-lg text-[var(--brand-green)]">All articles ({posts.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-stone)] bg-[#FAF8F2]">
                <th className="py-2 px-3">Title</th>
                <th className="py-2 px-3">Trade · City</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3 text-right">Views</th>
                <th className="py-2 px-3">Updated</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[var(--text-muted)]">No articles yet. Generate one above.</td></tr>
              ) : posts.map((p) => (
                <tr key={p.post_id} className="border-b border-[var(--border-stone)] last:border-0">
                  <td className="py-2 px-3">
                    <div className="font-semibold text-[var(--brand-green)] line-clamp-1">{p.title}</div>
                    <div className="text-xs text-[var(--text-muted)] line-clamp-1">/blog/{p.slug}</div>
                  </td>
                  <td className="py-2 px-3 text-xs text-[var(--text-muted)]">{p.trade} · {p.city}</td>
                  <td className="py-2 px-3">
                    <span className={
                      p.status === "published" ? "text-[#0A7048] font-semibold" :
                      p.status === "draft" ? "text-[var(--brand-orange)] font-semibold" :
                      "text-[var(--text-muted)]"
                    }>{p.status}</span>
                  </td>
                  <td className="py-2 px-3 text-right">{p.views || 0}</td>
                  <td className="py-2 px-3 text-xs text-[var(--text-muted)] whitespace-nowrap">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString("en-GB") : "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View" className="p-1.5 hover:bg-[var(--sand-warm)] rounded" data-testid={`blog-view-${p.slug}`}>
                        <ArrowSquareOut size={16} className="text-[var(--brand-green)]" />
                      </a>
                      <button onClick={() => togglePublish(p)} title={p.status === "published" ? "Unpublish" : "Publish"} className="p-1.5 hover:bg-[var(--sand-warm)] rounded" data-testid={`blog-toggle-${p.slug}`}>
                        {p.status === "published" ? <EyeSlash size={16} className="text-[var(--brand-orange)]" /> : <Eye size={16} className="text-[#0A7048]" />}
                      </button>
                      <button onClick={() => openEdit(p)} title="Edit" className="p-1.5 hover:bg-[var(--sand-warm)] rounded" data-testid={`blog-edit-${p.slug}`}>
                        <PencilSimple size={16} className="text-[var(--brand-green)]" />
                      </button>
                      <button onClick={() => del(p)} title="Delete" className="p-1.5 hover:bg-red-50 rounded" data-testid={`blog-delete-${p.slug}`}>
                        <Trash size={16} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditModal post={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
